import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './PathologyResultPage.css';
import pathologyResultService from '../services/pathologyResultService';
import PatientWorklistGrid from './pathology/components/PatientWorklistGrid';
import PatientSummaryHeader from './pathology/components/PatientSummaryHeader';
import TabWiseResultEntry from './pathology/components/TabWiseResultEntry';
import StackedResultEntry from './pathology/components/StackedResultEntry';
import CompactGridResultEntry from './pathology/components/CompactGridResultEntry';
import ResultActionBar from './pathology/components/ResultActionBar';
import PreviousResultDrawer from './pathology/components/PreviousResultDrawer';
import RevisionModal from './pathology/components/RevisionModal';
import AuditTrailModal from './pathology/components/AuditTrailModal';
import PathologyReportPreviewModal from './pathology/components/PathologyReportPreviewModal';
import ParameterHistoryModal from './pathology/components/ParameterHistoryModal';
import PatientOrder360Modal from '../components/common/PatientOrder360Modal';
import RecordOutsourceModal from './pathology/components/RecordOutsourceModal';

export const PathologyResultPage = () => {
  // ── Worklist Filtering State ──────────────────────────────────────────────
  const [searchField, setSearchField] = useState('ALL');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING'); // PENDING, DRAFT, APPROVED, ALL
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [rawWorklist, setRawWorklist] = useState([]);
  const [selectedLogPatient, setSelectedLogPatient] = useState(null);

  // ── Summary KPI Metrics ───────────────────────────────────────────────────
  const [summary, setSummary] = useState({
    pendingCount: 0,
    draftCount: 0,
    approvedTodayCount: 0,
    criticalTodayCount: 0,
    totalEligibleToday: 0,
  });

  // ── Active Patient & Layout Format ────────────────────────────────────────
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [layoutFormat, setLayoutFormat] = useState(() => {
    return localStorage.getItem('lis_pathology_layout_pref') || 'tab-wise';
  });

  // ── Active Test Tab & Test-Level Checkbox Selection ───────────────────────
  const [activeTestId, setActiveTestId] = useState(null);
  const [selectedTestIds, setSelectedTestIds] = useState(new Set());

  // ── In-Memory Observation Cache: testId -> array of observations ──────────
  const [testObservationsMap, setTestObservationsMap] = useState({});
  const [instrumentResultsMap, setInstrumentResultsMap] = useState({});
  const [formDetailMap, setFormDetailMap] = useState({});
  const [loadingForm, setLoadingForm] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(false);

  // ── Revision & Audit Trail Modals ─────────────────────────────────────────
  const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [auditTrailList, setAuditTrailList] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // ── Previous Results Drawer & Report Preview ──────────────────────────────
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [patientHistoryList, setPatientHistoryList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // ── Parameter History & Delta Check Modal ─────────────────────────────────
  const [isParamHistoryOpen, setIsParamHistoryOpen] = useState(false);
  const [paramHistoryData, setParamHistoryData] = useState(null);
  const [loadingParamHistory, setLoadingParamHistory] = useState(false);

  // ── Outsource Result Recording & Report Attachment Modal ───────────────────
  const [recordOutsourceModalOpen, setRecordOutsourceModalOpen] = useState(false);
  const [activeOutsourceRecordTest, setActiveOutsourceRecordTest] = useState(null);

  // ── Toast Notification ────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };

  // Persist layout format preference
  const handleChangeLayout = (format) => {
    setLayoutFormat(format);
    localStorage.setItem('lis_pathology_layout_pref', format);
  };

  // 1. Fetch Summary Metrics
  const fetchSummary = useCallback(async () => {
    try {
      const res = await pathologyResultService.getSummary();
      if (res.success && res.data) {
        setSummary(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch pathology summary metrics:', err);
    }
  }, []);

  // 2. Fetch Raw Worklist Items
  const fetchWorklist = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = {
        status: statusFilter,
        pageNumber: 1,
        pageSize: 100,
      };

      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const trimmed = searchValue.trim();
      if (trimmed) {
        switch (searchField) {
          case 'UHID':
            params.uhid = trimmed;
            break;
          case 'LABNO':
            params.ledgerTransactionNo = trimmed;
            break;
          case 'PATIENT':
            params.patientName = trimmed;
            break;
          case 'PANEL':
            params.panelName = trimmed;
            break;
          case 'BARCODE':
            params.barcodeNo = trimmed;
            break;
          case 'SAMPLENO':
            params.sampleNo = trimmed;
            break;
          case 'INVESTIGATION':
            params.investigationName = trimmed;
            break;
          case 'ALL':
          default:
            params.searchQuery = trimmed;
            break;
        }
      }

      const res = await pathologyResultService.searchWorklist(params);
      if (res.success && res.data) {
        setRawWorklist(res.data.items || []);
      } else {
        setRawWorklist([]);
      }
    } catch (err) {
      console.error('Failed to load pathology worklist:', err);
      showToast(err.response?.data?.message || 'Error loading worklist.', 'error');
      setRawWorklist([]);
    } finally {
      setLoadingList(false);
    }
  }, [statusFilter, searchField, searchValue, fromDate, toDate]);

  useEffect(() => {
    fetchSummary();
    fetchWorklist();
  }, [fetchSummary, fetchWorklist]);

  // 3. Group Raw Worklist Items into Distinct Patients
  const patients = useMemo(() => {
    const map = new Map();

    rawWorklist.forEach((item) => {
      const key = item.ledgerTransactionNo || item.patientId || `order_${item.testId}`;
      if (!map.has(key)) {
        map.set(key, {
          ledgerTransactionNo: item.ledgerTransactionNo,
          patientId: item.patientId,
          patientName: item.patientName,
          ageGender: item.ageGender,
          age: item.age,
          gender: item.gender,
          mobile: item.mobile,
          panelId: item.panelId,
          panelName: item.panelName || 'Direct Walk-In',
          barcodeNo: item.barcodeNo,
          sampleReceiveDate: item.sampleReceiveDate,
          sampleReceiver: item.sampleReceiver,
          sampleCollectionDate: item.sampleCollectionDate,
          sampleCollector: item.sampleCollector,
          isSampleCollected: item.isSampleCollected,
          sampleTypeName: item.sampleTypeName,
          departmentName: item.departmentName,
          doctorName: item.doctorName,
          centreName: item.centreName,
          isOutsourced: Boolean(item.isOutsourced),
          outsourceStatus: item.outsourceStatus,
          referenceLabName: item.referenceLabName,
          isRejected: Boolean(item.isRejected || item.isSampleCollected === 'R'),
          sampleRejectionReason: item.sampleRejectionReason,
          tests: [],
        });
      }

      const p = map.get(key);
      p.tests.push({
        testId: item.testId,
        itemId: item.itemId,
        itemCode: item.itemCode,
        itemName: item.itemName,
        investigationId: item.investigationId,
        investigationName: item.investigationName || item.itemName || '',
        panelId: item.panelId,
        panelName: item.panelName || p.panelName || 'Direct Walk-In',
        departmentName: item.departmentName,
        barcodeNo: item.barcodeNo,
        sampleTypeName: item.sampleTypeName,
        containerName: item.containerName,
        containerColor: item.containerColor,
        isSampleCollected: item.isSampleCollected,
        sampleCollectionDate: item.sampleCollectionDate,
        sampleCollector: item.sampleCollector,
        sampleReceiveDate: item.sampleReceiveDate,
        sampleReceiver: item.sampleReceiver,
        resultFlag: item.resultFlag,
        approved: item.approved,
        status: item.status,
        hasCriticalResult: item.hasCriticalResult,
        isOutsourced: Boolean(item.isOutsourced),
        outsourceStatus: item.outsourceStatus,
        referenceLabName: item.referenceLabName,
        externalResultSummary: item.externalResultSummary,
        externalAttachmentUrl: item.externalReportAttachmentUrl || item.externalAttachmentUrl,
        isRejected: Boolean(item.isRejected || item.isSampleCollected === 'R'),
        rejectionReason: item.sampleRejectionReason,
      });
    });

    return Array.from(map.values());
  }, [rawWorklist]);

  // Calculate live flag for an observation value
  const calculateFlag = useCallback((val, obs) => {
    if (val === null || val === undefined || val === '') return 'N';

    const fmt = obs.readingFormat?.toLowerCase();
    if (fmt === 'numeric') {
      const dVal = parseFloat(val);
      if (isNaN(dVal)) return 'N';

      const minCrit = obs.minCritical ? parseFloat(obs.minCritical) : null;
      const maxCrit = obs.maxCritical ? parseFloat(obs.maxCritical) : null;
      const minRef = obs.minReading ? parseFloat(obs.minReading) : null;
      const maxRef = obs.maxReading ? parseFloat(obs.maxReading) : null;

      if (minCrit !== null && dVal <= minCrit) return 'LL';
      if (maxCrit !== null && dVal >= maxCrit) return 'HH';
      if (minRef !== null && dVal < minRef) return 'L';
      if (maxRef !== null && dVal > maxRef) return 'H';
      return 'N';
    }

    if (fmt === 'option') {
      if (obs.abnormalValue && obs.abnormalValue.toLowerCase() === val.toLowerCase()) {
        return 'A';
      }
      return 'N';
    }

    if (fmt === 'positive/negative') {
      const lower = val.toLowerCase();
      if (lower === 'positive') return 'H';
      if (lower.includes('equivocal') || lower.includes('borderline')) return 'A';
      return 'N';
    }

    return 'N';
  }, []);

  // 4. Load Observation Form for a specific test item (with cache check)
  const loadTestForm = useCallback(
    async (testId) => {
      // If already loaded in memory, return cached
      if (testObservationsMap[testId]) {
        return testObservationsMap[testId];
      }

      setLoadingForm(true);
      try {
        const res = await pathologyResultService.getTestResultForm(testId);
        if (res.success && res.data) {
          setFormDetailMap((prev) => ({ ...prev, [testId]: res.data }));
          const mappedObs = (res.data.observations || []).map((o) => {
            const initialVal =
              o.value !== null && o.value !== undefined
                ? o.value
                : o.defaultValue || '';
            const initialFlag = o.flag || calculateFlag(initialVal, o);
            return {
              ...o,
              currentValue: initialVal,
              currentFlag: initialFlag,
              currentRemarks: o.remarks || '',
              isCritical: initialFlag === 'LL' || initialFlag === 'HH',
            };
          });

          setTestObservationsMap((prev) => ({ ...prev, [testId]: mappedObs }));


          return mappedObs;
        }
      } catch (err) {
        console.error(`Failed to load result form for test #${testId}:`, err);
        showToast(err.response?.data?.message || `Error loading form for test #${testId}`, 'error');
      } finally {
        setLoadingForm(false);
      }
      return [];
    },
    [calculateFlag, testObservationsMap]
  );

  // 5. Select Patient from Grid
  const handleSelectPatient = useCallback(
    async (patient) => {
      let patientToSelect = { ...patient, tests: [...(patient.tests || [])] };

      // Additively fetch all tests belonging to this order (in-house, outsourced, rejected)
      if (patient.ledgerTransactionNo) {
        try {
          const orderTestsRes = await pathologyResultService.getOrderTests(patient.ledgerTransactionNo);
          if (orderTestsRes && orderTestsRes.success && Array.isArray(orderTestsRes.data) && orderTestsRes.data.length > 0) {
            if (orderTestsRes.data[0]?.panelName) {
              patientToSelect.panelName = orderTestsRes.data[0].panelName;
            }
            if (orderTestsRes.data[0]?.panelId) {
              patientToSelect.panelId = orderTestsRes.data[0].panelId;
            }
            patientToSelect.tests = orderTestsRes.data.map((item) => ({
              testId: item.testId,
              itemId: item.itemId,
              itemCode: item.itemCode,
              itemName: item.itemName,
              investigationId: item.investigationId,
              investigationName: item.investigationName || item.itemName || '',
              panelId: item.panelId,
              panelName: item.panelName || patientToSelect.panelName || 'Direct Walk-In',
              departmentName: item.departmentName,
              barcodeNo: item.barcodeNo,
              sampleTypeName: item.sampleTypeName,
              containerName: item.containerName,
              containerColor: item.containerColor,
              isSampleCollected: item.isSampleCollected,
              sampleCollectionDate: item.sampleCollectionDate,
              sampleCollector: item.sampleCollector,
              sampleReceiveDate: item.sampleReceiveDate,
              sampleReceiver: item.sampleReceiver,
              resultFlag: item.resultFlag,
              approved: item.approved,
              status: item.status,
              hasCriticalResult: item.hasCriticalResult,
              isOutsourced: Boolean(item.isOutsourced),
              outsourceStatus: item.outsourceStatus,
              referenceLabName: item.referenceLabName,
              externalResultSummary: item.externalResultSummary,
              externalAttachmentUrl: item.externalReportAttachmentUrl || item.externalAttachmentUrl,
              isRejected: Boolean(item.isRejected || item.isSampleCollected === 'R'),
              rejectionReason: item.sampleRejectionReason,
            }));
          }
        } catch (err) {
          console.warn('Could not fetch complete order tests for patient:', err);
        }
      }

      setSelectedPatient(patientToSelect);
      setIsParamHistoryOpen(false);
      setParamHistoryData(null);
      if (patientToSelect.tests && patientToSelect.tests.length > 0) {
        const firstTestId = patientToSelect.tests[0].testId;
        setActiveTestId(firstTestId);

        // By default, check only tests eligible for direct in-house result entry
        const editableIds = new Set(
          patientToSelect.tests
            .filter((t) => !t.isOutsourced && !t.isRejected)
            .map((t) => t.testId)
        );
        setSelectedTestIds(editableIds.size > 0 ? editableIds : new Set([firstTestId]));

        // Preload forms for all tests of this patient
        for (const t of patientToSelect.tests) {
          await loadTestForm(t.testId);
        }
      } else {
        setActiveTestId(null);
        setSelectedTestIds(new Set());
      }
    },
    [loadTestForm]
  );

  // Handle Tab / Test Click
  const handleSelectTestTab = (testId) => {
    setActiveTestId(testId);
    loadTestForm(testId);
  };

  // 6. Test-Level Checkbox Selection (CRITICAL)
  const handleToggleTestCheckbox = (testId) => {
    setSelectedTestIds((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  };

  const handleSelectAllTests = () => {
    if (selectedPatient && selectedPatient.tests) {
      setSelectedTestIds(new Set(selectedPatient.tests.map((t) => t.testId)));
    }
  };

  const handleDeselectAllTests = () => {
    setSelectedTestIds(new Set());
  };

  // 7. Value & Remarks Change in Memory
  const handleObservationChange = (testId, obsId, newVal) => {
    setTestObservationsMap((prev) => {
      const currentList = prev[testId] || [];
      const updatedList = currentList.map((item) => {
        if (item.labObservationId === obsId) {
          const liveFlag = calculateFlag(newVal, item);
          return {
            ...item,
            currentValue: newVal,
            currentFlag: liveFlag,
            isCritical: liveFlag === 'LL' || liveFlag === 'HH',
          };
        }
        return item;
      });
      return { ...prev, [testId]: updatedList };
    });
  };

  const handleRemarksChange = (testId, obsId, newRemarks) => {
    setTestObservationsMap((prev) => {
      const currentList = prev[testId] || [];
      const updatedList = currentList.map((item) =>
        item.labObservationId === obsId ? { ...item, currentRemarks: newRemarks } : item
      );
      return { ...prev, [testId]: updatedList };
    });
  };

  // Auto-Fill Normal / Default Values for a test
  const handleFillDefaults = (testId) => {
    setTestObservationsMap((prev) => {
      const currentList = prev[testId] || [];
      const updatedList = currentList.map((item) => {
        let fillVal = item.currentValue;
        if (!fillVal) {
          if (item.defaultValue) {
            fillVal = item.defaultValue;
          } else if (
            item.readingFormat?.toLowerCase() === 'numeric' &&
            item.minReading &&
            item.maxReading
          ) {
            const min = parseFloat(item.minReading);
            const max = parseFloat(item.maxReading);
            if (!isNaN(min) && !isNaN(max)) {
              fillVal = ((min + max) / 2).toFixed(item.roundUpto || 0);
            }
          } else if (item.readingFormat?.toLowerCase() === 'positive/negative') {
            fillVal = 'Negative';
          } else if (item.readingFormat?.toLowerCase() === 'option' && item.options?.length > 0) {
            fillVal = item.options[0].text;
          }
        }
        const liveFlag = calculateFlag(fillVal, item);
        return {
          ...item,
          currentValue: fillVal,
          currentFlag: liveFlag,
          isCritical: liveFlag === 'LL' || liveFlag === 'HH',
        };
      });
      return { ...prev, [testId]: updatedList };
    });
    showToast('Applied normal / default values for unpopulated parameters.', 'info');
  };

  // 8. Save Draft Action (APPLIES STRICTLY TO CHECKED IN-HOUSE TESTS ONLY)
  const handleSaveDraft = async () => {
    const checkedIds = Array.from(selectedTestIds).filter((id) => {
      const t = selectedPatient?.tests?.find((x) => x.testId === id);
      return t && !t.isOutsourced && !t.isRejected;
    });
    if (checkedIds.length === 0) {
      showToast('No eligible in-house investigations selected. Outsourced and rejected tests cannot be result-entered.', 'warning');
      return;
    }

    setSubmittingAction(true);
    let successCount = 0;
    let errorCount = 0;

    for (const testId of checkedIds) {
      const obsList = testObservationsMap[testId] || [];
      const payload = {
        observations: obsList.map((o) => ({
          labObservationId: o.labObservationId,
          value: o.currentValue !== undefined && o.currentValue !== null ? o.currentValue.toString() : '',
          remarks: o.currentRemarks || undefined,
        })),
        action: 'Draft',
        interpretationRemarks: formDetailMap[testId]?.interpretationRemarks || 'Batch Saved Draft',
      };

      try {
        const res = await pathologyResultService.saveDraft(testId, payload);
        if (res.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        console.error(`Error saving draft for test #${testId}:`, err);
        errorCount++;
      }
    }

    setSubmittingAction(false);
    if (successCount > 0) {
      showToast(`Successfully saved draft for ${successCount} selected test(s).`, 'success');
      fetchSummary();
      fetchWorklist();
    }
    if (errorCount > 0) {
      showToast(`Failed to save draft for ${errorCount} test(s). Check parameters.`, 'error');
    }
  };

  // 9. Save & Approve / Finalize Action (APPLIES STRICTLY TO CHECKED IN-HOUSE TESTS ONLY)
  const handleFinalizeApprove = async () => {
    const checkedIds = Array.from(selectedTestIds).filter((id) => {
      const t = selectedPatient?.tests?.find((x) => x.testId === id);
      return t && !t.isOutsourced && !t.isRejected;
    });
    if (checkedIds.length === 0) {
      showToast('No eligible in-house investigations selected. Outsourced and rejected tests cannot be approved here.', 'warning');
      return;
    }

    // Client-side mandatory validation for all checked tests
    for (const testId of checkedIds) {
      const obsList = testObservationsMap[testId] || [];
      const missing = obsList.filter(
        (o) => o.resultRequired && (!o.currentValue || o.currentValue.toString().trim() === '')
      );
      if (missing.length > 0) {
        const testObj = selectedPatient?.tests?.find((t) => t.testId === testId);
        showToast(
          `Cannot approve "${testObj?.investigationName || testId}": Missing required parameter(s) (${missing
            .map((m) => m.name)
            .join(', ')}).`,
          'warning'
        );
        return;
      }
    }

    setSubmittingAction(true);
    let successCount = 0;
    let errorCount = 0;

    for (const testId of checkedIds) {
      const obsList = testObservationsMap[testId] || [];
      const payload = {
        observations: obsList.map((o) => ({
          labObservationId: o.labObservationId,
          value: o.currentValue !== undefined && o.currentValue !== null ? o.currentValue.toString() : '',
          remarks: o.currentRemarks || undefined,
        })),
        action: 'Finalize',
        interpretationRemarks: formDetailMap[testId]?.interpretationRemarks || 'Clinically Approved',
      };

      try {
        const res = await pathologyResultService.finalizeResult(testId, payload);
        if (res.success) {
          successCount++;
          // Update status in local test object
          if (selectedPatient) {
            const tObj = selectedPatient.tests?.find((t) => t.testId === testId);
            if (tObj) {
              tObj.approved = 1;
              tObj.status = 'Approved';
            }
          }
        } else {
          errorCount++;
        }
      } catch (err) {
        console.error(`Error finalizing test #${testId}:`, err);
        errorCount++;
      }
    }

    setSubmittingAction(false);
    if (successCount > 0) {
      showToast(`Successfully finalized and approved ${successCount} test(s)!`, 'success');
      fetchSummary();
      fetchWorklist();
    }
    if (errorCount > 0) {
      showToast(`Approval failed for ${errorCount} test(s). Check laboratory constraints.`, 'error');
    }
  };

  // 10. Clinical Revision Modal Actions
  const handleOpenRevise = () => {
    setRevisionReason('');
    setIsRevisionModalOpen(true);
  };

  const handleConfirmRevision = async () => {
    if (!revisionReason.trim()) {
      showToast('Revision reason is mandatory.', 'warning');
      return;
    }

    const checkedIds = Array.from(selectedTestIds);
    setSubmittingAction(true);
    let successCount = 0;

    for (const testId of checkedIds) {
      const obsList = testObservationsMap[testId] || [];
      const payload = {
        observations: obsList.map((o) => ({
          labObservationId: o.labObservationId,
          value: o.currentValue !== undefined && o.currentValue !== null ? o.currentValue.toString() : '',
          remarks: o.currentRemarks || undefined,
        })),
        action: 'Revise',
        revisionReason: revisionReason.trim(),
      };

      try {
        const res = await pathologyResultService.reviseResult(testId, payload);
        if (res.success) successCount++;
      } catch (err) {
        console.error(`Error revising test #${testId}:`, err);
      }
    }

    setSubmittingAction(false);
    setIsRevisionModalOpen(false);
    if (successCount > 0) {
      showToast(`Successfully revised ${successCount} test report(s) with audit record.`, 'success');
      fetchSummary();
      fetchWorklist();
    }
  };

  // 11. Audit Trail Modal Actions
  const loadAuditForTest = async (testId) => {
    if (!testId) return;
    setLoadingAudit(true);
    try {
      const res = await pathologyResultService.getAuditTrail(testId);
      if (res.success && res.data) {
        setAuditTrailList(res.data);
      } else {
        setAuditTrailList([]);
      }
    } catch (err) {
      console.error('Failed to load audit trail:', err);
      setAuditTrailList([]);
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleOpenAudit = async (customTestId = null) => {
    const targetTestId = customTestId || activeTestId || Array.from(selectedTestIds)[0];
    if (!targetTestId) return;

    setIsAuditModalOpen(true);
    await loadAuditForTest(targetTestId);
  };

  // Open Previous Patient Laboratory History Drawer
  const handleOpenHistory = async () => {
    if (!selectedPatient?.patientId) return;
    setIsHistoryDrawerOpen(true);
    setLoadingHistory(true);
    try {
      const res = await pathologyResultService.getPatientHistory(
        selectedPatient.patientId,
        selectedPatient.ledgerTransactionNo || selectedPatient.labNo
      );
      if (res.success && res.data) {
        setPatientHistoryList(res.data);
      } else {
        setPatientHistoryList([]);
      }
    } catch (err) {
      console.error('Failed to load patient prior history:', err);
      setPatientHistoryList([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // 12. Print / Preview Pathology Diagnostic Report
  const handlePrintReport = () => {
    setIsReportModalOpen(true);
  };

  // 13. Save & Next Test navigation
  const handleSaveAndNext = async () => {
    if (!activeTestId) return;
    const currentList = testObservationsMap[activeTestId] || [];
    const payload = {
      observations: currentList.map((o) => ({
        labObservationId: o.labObservationId,
        value: o.currentValue !== undefined && o.currentValue !== null ? o.currentValue.toString() : '',
        remarks: o.currentRemarks || undefined,
      })),
      action: 'SaveDraft',
    };

    setSubmittingAction(true);
    try {
      const res = await pathologyResultService.saveDraft(activeTestId, payload);
      if (res.success) {
        showToast('Draft saved successfully.', 'success');
        fetchWorklist();
        if (selectedPatient?.tests) {
          const cIdx = selectedPatient.tests.findIndex((t) => t.testId === activeTestId);
          if (cIdx >= 0 && cIdx < selectedPatient.tests.length - 1) {
            const nextTest = selectedPatient.tests[cIdx + 1];
            if (nextTest) {
              setActiveTestId(nextTest.testId);
              loadTestForm(nextTest.testId);
              showToast(`Navigated to next test: "${nextTest.investigationName}".`, 'info');
            } else {
              showToast('All tests for this patient reviewed.', 'info');
            }
          } else {
            showToast('All tests reviewed for this patient.', 'info');
          }
        }
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
      showToast(err.response?.data?.message || 'Error saving draft', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // 14. Record / Attach External Outsource Result
  const handleOpenRecordOutsourceModal = (test) => {
    setActiveOutsourceRecordTest(test);
    setRecordOutsourceModalOpen(true);
  };

  const handleRecordOutsourceSuccess = (savedData) => {
    setSelectedPatient((prev) => {
      if (!prev) return prev;
      const updatedTests = (prev.tests || []).map((t) => {
        if (t.testId === savedData.testId) {
          return {
            ...t,
            externalResultSummary: savedData.externalResultSummary,
            externalAttachmentUrl: savedData.externalAttachmentUrl,
            outsourceStatus: savedData.outsourceStatus,
            resultFlag: savedData.resultFlag,
          };
        }
        return t;
      });
      return { ...prev, tests: updatedTests };
    });
    setRawWorklist((prev) =>
      prev.map((item) => {
        if (item.testId === savedData.testId) {
          return {
            ...item,
            externalResultSummary: savedData.externalResultSummary,
            externalReportAttachmentUrl: savedData.externalAttachmentUrl,
            outsourceStatus: savedData.outsourceStatus,
            resultFlag: savedData.resultFlag,
          };
        }
        return item;
      })
    );
  };

  // ── Open Parameter History & Delta Check Modal ────────────────────────────
  const handleOpenParameterHistory = async (obs, testId) => {
    if (!selectedPatient?.patientId || !obs?.labObservationId) return;
    setIsParamHistoryOpen(true);
    setLoadingParamHistory(true);
    setParamHistoryData(null);
    const currentTest = testId || activeTestId;
    try {
      const res = await pathologyResultService.getParameterHistory(
        selectedPatient.patientId,
        obs.labObservationId,
        currentTest,
        5
      );
      if (res.success && res.data) {
        const payload = { ...res.data };
        // If current value was edited in memory UI, reflect in payload
        const liveObs = (testObservationsMap[currentTest] || []).find(
          (o) => o.labObservationId === obs.labObservationId
        );
        const liveValue = liveObs?.currentValue !== undefined ? liveObs.currentValue : obs.currentValue ?? obs.value;
        if (liveValue !== undefined && liveValue !== null && payload.currentResult) {
          payload.currentResult.value = liveValue.toString();
          const num = parseFloat(liveValue);
          payload.currentResult.numericValue = !isNaN(num) ? num : null;
          if (payload.previousResult && payload.previousResult.numericValue !== null && !isNaN(num)) {
            const prevNum = payload.previousResult.numericValue;
            payload.absoluteChange = Math.round((num - prevNum) * 100) / 100;
            if (prevNum !== 0) {
              const pct = Math.round(((num - prevNum) / prevNum) * 1000) / 10;
              payload.percentageChange = pct;
              payload.deltaStatus = Math.abs(pct) >= 10 ? 'DELTA_ALERT' : 'NORMAL';
              payload.deltaMessage =
                Math.abs(pct) >= 10 ? 'Significant change from previous result' : 'Within expected variance';
            }
          }
        }
        setParamHistoryData(payload);
      } else {
        showToast('No history records found for this parameter.', 'info');
      }
    } catch (err) {
      console.error('Failed to load parameter history:', err);
    } finally {
      setLoadingParamHistory(false);
    }
  };

  // Enrich tests with actual clinical investigation names from loaded form detail or master codes
  const enrichedTests = useMemo(() => {
    if (!selectedPatient || !selectedPatient.tests) return [];
    const KNOWN_CODES = {
      URINE_RM: 'Complete Urine Examination',
      CBC: 'Complete Blood Count (CBC)',
      LFT: 'Liver Function Test (LFT)',
      KFT: 'Kidney / Renal Function Test (KFT)',
      LIPID: 'Lipid Profile',
      THYROID: 'Thyroid Profile (T3, T4, TSH)',
      MIC_URINE_AST: 'Urine Culture & Sensitivity (AST)',
      MIC_BLOOD_AST: 'Blood Culture & Sensitivity (AST)',
      MIC_SPUTUM_AST: 'Sputum Culture & Sensitivity (AST)',
      MIC_PUS_AST: 'Pus & Wound Swab Culture & AST',
      MIC_CSF_AST: 'CSF & Sterile Body Fluid Culture & AST',
    };

    return selectedPatient.tests.map((t) => {
      const detail = formDetailMap[t.testId];
      const resolvedName =
        (detail?.investigationName && detail.investigationName.trim() !== '' ? detail.investigationName : null) ||
        (detail?.itemName && detail.itemName.trim() !== '' ? detail.itemName : null) ||
        (t.investigationName && t.investigationName.trim() !== '' ? t.investigationName : null) ||
        (t.itemName && t.itemName.trim() !== '' ? t.itemName : null) ||
        KNOWN_CODES[t.itemCode] ||
        (t.itemCode && t.itemCode.trim() !== '' ? t.itemCode : null) ||
        `Test #${t.testId}`;

      const isOutsourced = Boolean(detail?.isOutsourced || t.isOutsourced);
      const isRejected = Boolean(detail?.isRejected || t.isRejected || t.isSampleCollected === 'R');

      return {
        ...t,
        investigationName: resolvedName,
        testName: resolvedName,
        itemCode: detail?.itemCode || t.itemCode,
        barcodeNo: detail?.barcodeNo || t.barcodeNo,
        departmentName: detail?.departmentName || t.departmentName || 'Pathology',
        isOutsourced,
        outsourceStatus: detail?.outsourceStatus || t.outsourceStatus,
        referenceLabName: detail?.referenceLabName || t.referenceLabName,
        externalResultSummary: detail?.externalResultSummary || t.externalResultSummary,
        externalAttachmentUrl: detail?.externalAttachmentUrl || t.externalAttachmentUrl,
        isRejected,
        rejectionReason: detail?.rejectionReason || detail?.sampleRejectionReason || t.rejectionReason,
      };
    });
  }, [selectedPatient, formDetailMap]);

  // Selection Metrics for Sticky Action Bar
  const selectedCount = selectedTestIds.size;
  const totalTests = selectedPatient?.tests?.length || 0;
  const hasApprovedSelected = selectedPatient?.tests?.some(
    (t) => selectedTestIds.has(t.testId) && t.approved === 1 && !t.isOutsourced && !t.isRejected
  );
  const hasDraftOrPendingSelected = selectedPatient?.tests?.some(
    (t) => selectedTestIds.has(t.testId) && t.approved === 0 && !t.isOutsourced && !t.isRejected
  );

  return (
    <div className="path-container">
      {/* Toast Banner */}
      {toast && (
        <div className={`path-toast path-toast-${toast.type}`}>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Screen 1: Patient Worklist Grid */}
      {!selectedPatient ? (
        <PatientWorklistGrid
          patients={patients}
          loading={loadingList}
          searchField={searchField}
          setSearchField={setSearchField}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          onRefresh={fetchWorklist}
          onSelectPatient={handleSelectPatient}
          onOpenLog={(patient) => {
            const formatted = {
              ...patient,
              samples: (patient.tests || []).map((t) => ({
                testId: t.testId,
                itemName: t.investigationName || t.testName || t.itemName,
                investigationName: t.investigationName || t.testName || t.itemName,
                departmentName: t.departmentName,
                barcodeNo: t.barcodeNo,
                sampleTypeName: t.sampleTypeName,
                containerName: t.containerName,
                isSampleCollected: t.isSampleCollected,
                sampleCollectionDate: t.sampleCollectionDate,
                sampleCollector: t.sampleCollector,
                sampleReceiveDate: t.sampleReceiveDate,
                sampleReceiver: t.sampleReceiver,
                isRejected: t.isRejected,
                sampleRejectionReason: t.rejectionReason || t.sampleRejectionReason,
                isOutsourced: t.isOutsourced,
                referenceLabName: t.referenceLabName,
                outsourceStatus: t.outsourceStatus,
                status: t.status,
                resultFlag: t.resultFlag,
                approved: t.approved,
              })),
            };
            setSelectedLogPatient(formatted);
          }}
          selectedPatientId={null}
          summary={summary}
        />
      ) : (
        /* Screen 2: Patient Result Entry Workspace */
        <div className="path-workspace">
          {/* Patient Summary Header */}
          <PatientSummaryHeader
            patient={selectedPatient}
            layoutFormat={layoutFormat}
            onChangeLayout={handleChangeLayout}
            onBackToList={() => {
              setSelectedPatient(null);
              setIsParamHistoryOpen(false);
              setParamHistoryData(null);
            }}
          />

          {/* Configurable Layout Presenter */}
          <div className="path-workspace-body">
            {layoutFormat === 'tab-wise' && (
              <TabWiseResultEntry
                tests={enrichedTests}
                activeTestId={activeTestId}
                onSelectTestTab={handleSelectTestTab}
                selectedTestIds={selectedTestIds}
                onToggleTestCheckbox={handleToggleTestCheckbox}
                testObservationsMap={testObservationsMap}
                instrumentResultsMap={instrumentResultsMap[activeTestId] || []}
                onObservationChange={handleObservationChange}
                onRemarksChange={handleRemarksChange}
                onFillDefaults={handleFillDefaults}
                onOpenParameterHistory={handleOpenParameterHistory}
                loadingForm={loadingForm}
                onOpenRecordOutsourceModal={handleOpenRecordOutsourceModal}
              />
            )}

            {layoutFormat === 'stacked' && (
              <StackedResultEntry
                tests={enrichedTests}
                selectedTestIds={selectedTestIds}
                onToggleTestCheckbox={handleToggleTestCheckbox}
                testObservationsMap={testObservationsMap}
                instrumentResultsMap={instrumentResultsMap}
                onObservationChange={handleObservationChange}
                onRemarksChange={handleRemarksChange}
                onFillDefaults={handleFillDefaults}
                onOpenParameterHistory={handleOpenParameterHistory}
              />
            )}

            {layoutFormat === 'compact' && (
              <CompactGridResultEntry
                tests={enrichedTests}
                activeTestId={activeTestId}
                onSelectTest={handleSelectTestTab}
                selectedTestIds={selectedTestIds}
                onToggleTestCheckbox={handleToggleTestCheckbox}
                testObservationsMap={testObservationsMap}
                instrumentResultsMap={instrumentResultsMap[activeTestId] || []}
                onObservationChange={handleObservationChange}
                onRemarksChange={handleRemarksChange}
                onFillDefaults={handleFillDefaults}
                onOpenParameterHistory={handleOpenParameterHistory}
              />
            )}
          </div>

          {/* Sticky Bottom Action Bar */}
          <ResultActionBar
            totalTests={totalTests}
            selectedCount={selectedCount}
            onSelectAll={handleSelectAllTests}
            onDeselectAll={handleDeselectAllTests}
            onSaveDraft={handleSaveDraft}
            onFinalizeApprove={handleFinalizeApprove}
            onOpenRevise={handleOpenRevise}
            onOpenAudit={handleOpenAudit}
            onOpenHistory={handleOpenHistory}
            onPrintReport={handlePrintReport}
            onSaveAndNext={handleSaveAndNext}
            submitting={submittingAction}
            hasApprovedSelected={hasApprovedSelected}
            hasDraftOrPendingSelected={hasDraftOrPendingSelected}
          />
        </div>
      )}

      {/* Auxiliary Modals & Drawer */}
      <RevisionModal
        isOpen={isRevisionModalOpen}
        onClose={() => setIsRevisionModalOpen(false)}
        onSubmit={handleConfirmRevision}
        reason={revisionReason}
        setReason={setRevisionReason}
        testName={
          selectedPatient?.tests?.find((t) => selectedTestIds.has(t.testId))?.investigationName ||
          'Selected Test'
        }
        submitting={submittingAction}
      />

      <AuditTrailModal
        isOpen={isAuditModalOpen}
        onClose={() => setIsAuditModalOpen(false)}
        auditList={auditTrailList}
        loading={loadingAudit}
        testName={
          enrichedTests?.find((t) => t.testId === (activeTestId || Array.from(selectedTestIds)[0]))?.investigationName || ''
        }
        tests={enrichedTests}
        activeTestId={activeTestId || Array.from(selectedTestIds)[0]}
        onChangeTest={(tId) => {
          setActiveTestId(tId);
          loadAuditForTest(tId);
        }}
      />

      <PreviousResultDrawer
        isOpen={isHistoryDrawerOpen}
        onClose={() => setIsHistoryDrawerOpen(false)}
        patient={selectedPatient}
        tests={enrichedTests}
        activeTestId={activeTestId}
        testObservationsMap={testObservationsMap}
        formDetailMap={formDetailMap}
        historyItems={patientHistoryList}
        loading={loadingHistory}
        onChangeTest={(tId) => {
          setActiveTestId(tId);
          loadTestForm(tId);
        }}
      />

      {/* High-Fidelity NABL Diagnostic Report Modal */}
      <PathologyReportPreviewModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        patient={selectedPatient}
        tests={(selectedPatient?.tests || []).map(t => ({
          ...t,
          isOutsourced: formDetailMap[t.testId]?.isOutsourced,
          externalResultSummary: formDetailMap[t.testId]?.externalResultSummary,
          externalAttachmentUrl: formDetailMap[t.testId]?.externalAttachmentUrl,
        }))}
        testObservationsMap={testObservationsMap}
      />

      {/* Parameter History & Delta Check Modal */}
      {selectedPatient && isParamHistoryOpen && (
        <ParameterHistoryModal
          isOpen={isParamHistoryOpen}
          onClose={() => {
            setIsParamHistoryOpen(false);
            setParamHistoryData(null);
          }}
          data={paramHistoryData}
          loading={loadingParamHistory}
        />
      )}

      {/* Patient Visit & Sample Lifecycle Log Modal */}
      <PatientOrder360Modal
        isOpen={Boolean(selectedLogPatient)}
        onClose={() => setSelectedLogPatient(null)}
        patientData={selectedLogPatient}
      />

      {/* Record External Partner Result & Attachment Modal */}
      {recordOutsourceModalOpen && (
        <RecordOutsourceModal
          isOpen={recordOutsourceModalOpen}
          onClose={() => {
            setRecordOutsourceModalOpen(false);
            setActiveOutsourceRecordTest(null);
          }}
          testItem={activeOutsourceRecordTest}
          patient={selectedPatient}
          onSuccess={handleRecordOutsourceSuccess}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default PathologyResultPage;
